// src/components/NewCampaignModal.jsx
import React from "react";

const newCampaignModalStyles = `
.psl-modal-root {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65); /* dark overlay, no blur */
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 16px;
}

/* Card */
.psl-modal-card {
  width: 560px;
  max-width: 95vw;
  background: #020617; /* dark slate */
  color: #e5e7eb;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  box-shadow: 0 22px 55px rgba(15, 23, 42, 0.8);
  padding: 16px 18px 18px;
}

.psl-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.psl-modal-card h4 {
  margin: 0;
  font-size: 18px;
  letter-spacing: 0.02em;
  color: #f9fafb;
}

/* Buttons (match SequenceBoard look) */
.psl-modal-root .psl-btn {
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #020617;
  color: #e5e7eb;
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 700;
  cursor: pointer;
  font-size: 13px;
  transition:
    transform 0.12s ease,
    background 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.psl-modal-root .psl-btn:hover {
  background: #0f172a;
  transform: translateY(-1px);
  border-color: #e5e7eb;
}

.psl-modal-root .psl-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.psl-modal-root .psl-btn.primary {
  background: linear-gradient(90deg, #6E5BFF, #A78BFA);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 10px 24px rgba(110, 91, 255, 0.3);
}

/* Inputs */
.psl-modal-root .psl-input {
  appearance: none;
  width: 100%;
  background: rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.25;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.psl-modal-root .psl-input::placeholder {
  color: #64748b;
}

.psl-modal-root .psl-input:hover {
  border-color: #475569;
}

.psl-modal-root .psl-input:focus {
  border-color: #e5e7eb;
  box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.55);
  background: rgba(15, 23, 42, 0.95);
}

/* Rows */
.psl-modal-card .psl-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.psl-modal-card .psl-actions .psl-input {
  flex: 1;
}

/* Error text */
.psl-modal-error {
  color: #fecaca;
  margin-top: 8px;
  font-size: 13px;
}

/* Small screens */
@media (max-width: 480px) {
  .psl-modal-card {
    padding: 12px 14px 14px;
  }

  .psl-modal-card .psl-actions {
    flex-direction: column;
  }

  .psl-modal-root .psl-btn.primary {
    width: 100%;
    justify-content: center;
  }
}
`;

export default function NewCampaignModal({
  open,
  onClose,
  templates,
  tplLoading = false,
  tplErr = "",
  onCreate, // async (payload) => id
  defaultTemplateId = null,
}) {
  const [name, setName] = React.useState("");
  const [templateId, setTemplateId] = React.useState(defaultTemplateId || null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const firstId = defaultTemplateId || templates?.[0]?.id || null;
    setTemplateId(firstId);
  }, [open, templates, defaultTemplateId]);

  if (!open) return null;

  const submit = async () => {
    if (!name.trim()) {
      alert("Enter a campaign name");
      return;
    }
    if (!templateId) {
      alert("Pick a template.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        template_id: Number(templateId),
      };
      await onCreate(payload);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="psl-modal-root"
      role="dialog"
      aria-modal="true"
      aria-label="Create Campaign"
      style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.5)",
      display: "grid",
      placeItems: "center",
      zIndex: 1000,
    }}
    onClick={onClose}
    >
      <style>{newCampaignModalStyles}</style>
      <div
        className="psl-modal-card psl-camp-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="psl-modal-header">
          <h4>New Campaign</h4>
          <button className="psl-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="psl-actions" style={{ paddingTop: 12 }}>
          <input
            className="psl-input psl-input--campaign-name"
            placeholder='Campaign name (e.g. "EDM Playlists")'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="psl-actions">
          <button
            className="psl-btn primary psl-btn--create-campaign"
            disabled={busy}
            onClick={submit}
          >
            {busy ? "Creating…" : "Create"}
          </button>
        </div>

        {tplErr && (
          <div className="psl-modal-error">
            {tplErr}
          </div>
        )}
      </div>
    </div>
  );
}
